
import React from "react";
import { RssFeedContent, ContentOverride } from "@/types/rss";
import { RssLoadingSpinner } from "../rss/RssLoadingSpinner";
import { RssError } from "../rss/RssError";
import { RssGridLayout } from "../rss/RssGridLayout";
import { RssCarouselLayout } from "../rss/RssCarouselLayout";
import { RssMagazineLayout } from "../rss/RssMagazineLayout";
import { RssItem } from "../rss/RssItem";
import { RssItemType } from "../rss/types";

interface RssFeedPageProps {
  content: RssFeedContent;
}

const RssFeedPage: React.FC<RssFeedPageProps> = ({ content }) => {
  const [items, setItems] = React.useState<RssItemType[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  const applyContentPatterns = (text: string, overrides?: Record<string, ContentOverride>) => {
    if (!overrides) return text;
    
    let modifiedText = text;
    
    for (const override of Object.values(overrides)) {
      if (!override.enabled) continue;
      
      if (override.defaultText) {
        return override.defaultText;
      }
      
      if (override.patterns) {
        for (const { pattern, replacement, useRegex } of override.patterns) {
          if (useRegex) {
            try {
              const regex = new RegExp(pattern, 'g');
              modifiedText = modifiedText.replace(regex, replacement);
            } catch (error) {
              console.error('Invalid regex pattern:', pattern);
            }
          } else {
            modifiedText = modifiedText.replace(new RegExp(pattern, 'g'), replacement);
          }
        }
      }
    }
    
    return modifiedText;
  };

  const processItem = (item: any): RssItemType => ({
    ...item,
    title: applyContentPatterns(item.title, content.overrides),
    description: applyContentPatterns(item.description || '', content.overrides)
  });

  React.useEffect(() => {
    const fetchRss = async () => {
      try {
        if (!content || !content.url) {
          setError('כתובת RSS חסרה');
          setLoading(false);
          return;
        }

        const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(content.url)}`);
        const data = await response.json();
        
        if (data.status === 'ok' && Array.isArray(data.items)) {
          const validItems = data.items
            .filter(item => item && item.title)
            .slice(0, content.maxItems || 10)
            .map(processItem);
          
          setItems(validItems);
        } else {
          setError('טעינת RSS נכשלה');
        }
      } catch (err) {
        console.error('RSS feed error:', err);
        setError('טעינת RSS נכשלה');
      } finally {
        setLoading(false);
      }
    };

    fetchRss();
  }, [content?.url, content?.maxItems, content?.overrides]);

  if (loading) return <RssLoadingSpinner />;
  if (error) return <RssError message={error} />;
  if (!items || items.length === 0) {
    return (
      <div className="text-center p-4">
        <p>לא נמצאו פריטים בפיד RSS</p>
      </div>
    );
  }

  const containerStyle = {
    backgroundColor: content.customStyle?.backgroundColor,
    color: content.customStyle?.textColor,
    padding: content.customStyle?.padding,
  };

  return (
    <div className="max-w-4xl mx-auto" style={containerStyle}>
      {content.template === 'grid' ? (
        <RssGridLayout items={items} content={content} />
      ) : content.template === 'carousel' ? (
        <RssCarouselLayout items={items} content={content} />
      ) : content.template === 'magazine' ? (
        <RssMagazineLayout items={items} content={content} />
      ) : (
        <div className="space-y-6 p-4">
          {items.map((item, index) => (
            <RssItem key={index} item={item} content={content} />
          ))}
        </div>
      )}
    </div>
  );
};

export default RssFeedPage;
